/**
 * Shared shapes + allowlists for the storefront Appearance panel.
 *
 * Mirrors the backend's read-side sanitizer (public_storefront_custom.go) and
 * the storefront's looks table (storefront/src/lib/looks.ts). Change all three.
 */

export type StorefrontLook =
  | ''
  | 'counter'
  | 'bakery'
  | 'dhaba'
  | 'roastery'
  | 'night'
  | 'fresh'
  | 'heritage'
  | 'atelier'
  | 'pop'
  | 'shelf'
  | 'docket'
  | 'bistro'
export type StorefrontRadius = '' | 'sharp' | 'soft' | 'pill'
export type StorefrontMenuLayout = '' | 'grid' | 'list' | 'tiles' | 'board' | 'magazine' | 'gallery' | 'shelf' | 'rows'
export type StorefrontHeroVariant =
  | ''
  | 'split'
  | 'cover'
  | 'type'
  | 'minimal'
  | 'carousel'
  | 'poster'
  | 'collage'
  | 'fullbleed'
  | 'billboard'
  | 'lineup'
  | 'receipt'
  | 'side'
export type StorefrontCardStyle = '' | 'raised' | 'outline' | 'flat' | 'bare' | 'sticker'
export type StorefrontNavStyle = '' | 'pills' | 'tabs' | 'index'
export type StorefrontTexture = '' | 'none' | 'grain' | 'paper'
export type StorefrontButtonShape = '' | 'pill' | 'rounded' | 'square'
export type StorefrontHeadingCase = '' | 'normal' | 'upper'
export type StorefrontBackgroundFit = '' | 'cover' | 'tile'
export type StorefrontBackgroundStrength = '' | 'faint' | 'soft' | 'strong'
export type StorefrontSectionId =
  | 'featured'
  | 'deals'
  | 'menu'
  | 'about'
  | 'gallery'
  | 'reviews'
  | 'hours'
  | 'loyalty'

export interface StorefrontSection {
  id: StorefrontSectionId
  enabled: boolean
}

export interface StorefrontSocial {
  platform: string
  url: string
}

export interface StorefrontImage {
  id: string
  src: string
  caption?: string
}

export interface StorefrontConfig {
  hero_headline?: string
  hero_subcopy?: string
  about?: string
  font_heading?: string
  font_body?: string
  splash_phrase?: string
  socials?: StorefrontSocial[]
  layout?: { show_featured?: boolean; show_deals?: boolean }
  look?: string
  radius?: string
  menu_layout?: string
  hero_variant?: string
  card_style?: string
  nav_style?: string
  texture?: string
  canvas_light?: string
  canvas_dark?: string
  button_shape?: string
  heading_case?: string
  background_fit?: string
  background_strength?: string
  sections?: Array<{ id: string; enabled: boolean }>
  announcement?: { text?: string; url?: string }
  reviews?: { enabled?: boolean }
  seo_title?: string
  seo_description?: string
  pwa_short_name?: string
  app_icon_url?: string
}

export const SECTION_IDS: StorefrontSectionId[] = [
  'featured',
  'deals',
  'menu',
  'about',
  'gallery',
  'reviews',
  'hours',
  'loyalty',
]

export const MAX_STOREFRONT_IMAGES = 8

/** Fresh-tenant order and defaults (mirrors defaultSections on the backend). */
export function defaultSections(legacy?: { show_featured?: boolean; show_deals?: boolean }): StorefrontSection[] {
  return [
    { id: 'featured', enabled: legacy?.show_featured !== false },
    { id: 'deals', enabled: legacy?.show_deals !== false },
    { id: 'menu', enabled: true },
    { id: 'about', enabled: true },
    { id: 'hours', enabled: true },
    { id: 'gallery', enabled: false },
    { id: 'reviews', enabled: false },
    { id: 'loyalty', enabled: false },
  ]
}

/** Dedupe, drop unknown ids, force menu on, append anything missing (off). */
export function normalizeSections(
  raw: unknown,
  legacy?: { show_featured?: boolean; show_deals?: boolean },
): StorefrontSection[] {
  if (!Array.isArray(raw) || raw.length === 0) return defaultSections(legacy)
  const known = new Set<string>(SECTION_IDS)
  const seen = new Set<string>()
  const out: StorefrontSection[] = []
  for (const s of raw as Array<{ id?: unknown; enabled?: unknown }>) {
    const id = String(s?.id ?? '').trim().toLowerCase()
    if (!known.has(id) || seen.has(id)) continue
    seen.add(id)
    out.push({ id: id as StorefrontSectionId, enabled: id === 'menu' ? true : s?.enabled === true })
  }
  if (!seen.has('menu')) {
    out.push({ id: 'menu', enabled: true })
    seen.add('menu')
  }
  for (const id of SECTION_IDS) if (!seen.has(id)) out.push({ id, enabled: false })
  return out
}

export interface LookMeta {
  id: Exclude<StorefrontLook, ''>
  label: string
  blurb: string
  /** The kind of place the look suits, in a few words (theme picker). */
  bestFor: string
  canvas: string
  surface: string
  ink: string
  radius: Exclude<StorefrontRadius, ''>
  menuLayout: Exclude<StorefrontMenuLayout, ''>
  heroVariant: Exclude<StorefrontHeroVariant, ''>
  cardStyle: Exclude<StorefrontCardStyle, ''>
  navStyle: Exclude<StorefrontNavStyle, ''>
  texture: Exclude<StorefrontTexture, ''>
  buttonShape: Exclude<StorefrontButtonShape, ''>
  headingCase: Exclude<StorefrontHeadingCase, ''>
  fontHeading: string
  fontBody: string
  /** The look's own page colour in each mode (placeholders, contrast checks). */
  canvasLight: string
  canvasDark: string
  fonts: string
  darkFirst?: boolean
  /** The look sets its headings in the italic (Bistro). */
  headingItalic?: boolean
}

export const LOOKS: LookMeta[] = [
  {
    id: 'counter',
    label: 'Counter',
    blurb: 'Your brand colour as a bold block behind the name, photo overlapping it. Confident fast casual. The default.',
    bestFor: 'Fast casual, all-rounders',
    canvas: '#F4F4F0',
    surface: '#FFFFFF',
    ink: '#171613',
    radius: 'soft',
    menuLayout: 'grid',
    heroVariant: 'split',
    cardStyle: 'raised',
    navStyle: 'pills',
    texture: 'none',
    buttonShape: 'pill',
    headingCase: 'normal',
    fontHeading: 'Archivo',
    fontBody: 'Inter',
    canvasLight: '#F4F4F0',
    canvasDark: '#121211',
    fonts: 'Archivo and Inter',
  },
  {
    id: 'bakery',
    label: 'Bakery',
    blurb: 'Blush canvas, soft serif, photos in arched shop windows. Patisseries and dessert bars.',
    bestFor: 'Patisseries, dessert bars',
    canvas: '#FFF7F4',
    surface: '#FCEAE5',
    ink: '#401D26',
    radius: 'pill',
    menuLayout: 'tiles',
    heroVariant: 'split',
    cardStyle: 'flat',
    navStyle: 'pills',
    texture: 'none',
    buttonShape: 'pill',
    headingCase: 'normal',
    fontHeading: 'Fraunces',
    fontBody: 'DM Sans',
    canvasLight: '#FFF7F4',
    canvasDark: '#1C1113',
    fonts: 'Fraunces and DM Sans',
  },
  {
    id: 'dhaba',
    label: 'Dhaba',
    blurb: 'Truck-art borders, painted signboard categories, a price-board menu on paper. Karahi, BBQ, chai.',
    bestFor: 'Karahi, BBQ, chai',
    canvas: '#F1E3C4',
    surface: '#FAF1DC',
    ink: '#21160C',
    radius: 'sharp',
    menuLayout: 'board',
    heroVariant: 'poster',
    cardStyle: 'bare',
    navStyle: 'tabs',
    texture: 'paper',
    buttonShape: 'square',
    headingCase: 'normal',
    fontHeading: 'Bricolage Grotesque',
    fontBody: 'Nunito',
    canvasLight: '#F1E3C4',
    canvasDark: '#16110A',
    fonts: 'Bricolage Grotesque and Nunito',
  },
  {
    id: 'roastery',
    label: 'Roastery',
    blurb: 'Large serif name, a typeset list and a side index on desktop. Coffee and all-day cafés.',
    bestFor: 'Coffee, all-day cafés',
    canvas: '#F7F6F2',
    surface: '#FFFFFF',
    ink: '#161614',
    radius: 'sharp',
    menuLayout: 'list',
    heroVariant: 'minimal',
    cardStyle: 'bare',
    navStyle: 'index',
    texture: 'none',
    buttonShape: 'pill',
    headingCase: 'normal',
    fontHeading: 'Instrument Serif',
    fontBody: 'Inter',
    canvasLight: '#F7F6F2',
    canvasDark: '#10100F',
    fonts: 'Instrument Serif and Inter',
  },
  {
    id: 'night',
    label: 'Night',
    blurb: 'Dark with film grain, poster-size condensed type, photo-led menu. Late-night, burgers, bars.',
    bestFor: 'Late-night, burgers, bars',
    canvas: '#0D0C0C',
    surface: '#191817',
    ink: '#F4F0E9',
    radius: 'soft',
    menuLayout: 'magazine',
    heroVariant: 'cover',
    cardStyle: 'raised',
    navStyle: 'tabs',
    texture: 'grain',
    buttonShape: 'rounded',
    headingCase: 'upper',
    fontHeading: 'Big Shoulders Display',
    fontBody: 'Inter',
    canvasLight: '#F5F4F1',
    canvasDark: '#0D0C0C',
    fonts: 'Big Shoulders Display and Inter',
    darkFirst: true,
  },
  {
    id: 'fresh',
    label: 'Fresh',
    blurb: 'Products on brand-tinted rounds, a three-shape photo collage, rounded wide type. Juice, matcha, salads.',
    bestFor: 'Juice, matcha, salads',
    canvas: '#F6F8F3',
    surface: '#EAF0E6',
    ink: '#14241C',
    radius: 'pill',
    menuLayout: 'tiles',
    heroVariant: 'collage',
    cardStyle: 'flat',
    navStyle: 'pills',
    texture: 'none',
    buttonShape: 'pill',
    headingCase: 'normal',
    fontHeading: 'Unbounded',
    fontBody: 'Figtree',
    canvasLight: '#F6F8F3',
    canvasDark: '#0F1411',
    fonts: 'Unbounded and Figtree',
  },
  {
    id: 'heritage',
    label: 'Heritage',
    blurb: 'Deep bottle green, a centred classical serif, gold ornaments and a printed-menu board. Mughlai and fine dining.',
    bestFor: 'Mughlai, fine dining',
    canvas: '#10201A',
    surface: '#162A22',
    ink: '#F0E8D4',
    radius: 'sharp',
    menuLayout: 'board',
    heroVariant: 'type',
    cardStyle: 'bare',
    navStyle: 'tabs',
    texture: 'paper',
    buttonShape: 'square',
    headingCase: 'normal',
    fontHeading: 'Cormorant Garamond',
    fontBody: 'Karla',
    canvasLight: '#F3EFE4',
    canvasDark: '#10201A',
    fonts: 'Cormorant Garamond and Karla',
    darkFirst: true,
  },
  {
    id: 'atelier',
    label: 'Atelier',
    blurb: 'One photograph edge to edge, then a gallery of tall photos with light, quiet type. Patisserie, chocolate, plated food.',
    bestFor: 'Patisserie, plated food',
    canvas: '#F7F8F8',
    surface: '#FFFFFF',
    ink: '#1B1D21',
    radius: 'sharp',
    menuLayout: 'gallery',
    heroVariant: 'fullbleed',
    cardStyle: 'bare',
    navStyle: 'tabs',
    texture: 'none',
    buttonShape: 'pill',
    headingCase: 'normal',
    fontHeading: 'Jost',
    fontBody: 'Jost',
    canvasLight: '#F7F8F8',
    canvasDark: '#121315',
    fonts: 'Jost',
  },
  {
    id: 'pop',
    label: 'Pop',
    blurb: 'A billboard in your brand colour, a sticker photo, outlined cards with hard shadows and a running band of categories. Burgers, fried chicken, pizza.',
    bestFor: 'Burgers, fried chicken, pizza',
    canvas: '#FFFFFF',
    surface: '#FFFFFF',
    ink: '#000000',
    radius: 'soft',
    menuLayout: 'grid',
    heroVariant: 'billboard',
    cardStyle: 'sticker',
    navStyle: 'pills',
    texture: 'none',
    buttonShape: 'rounded',
    headingCase: 'normal',
    fontHeading: 'Baloo Bhaijaan 2',
    fontBody: 'Rubik',
    canvasLight: '#FFFFFF',
    canvasDark: '#000000',
    fonts: 'Baloo Bhaijaan 2 and Rubik',
  },
  {
    id: 'shelf',
    label: 'Shelf',
    blurb: 'Each category is a shelf you swipe sideways, products standing on a ledge in cool light. Drinks, bottles, desserts, grab and go.',
    bestFor: 'Drinks, desserts, grab and go',
    canvas: '#EFF3F5',
    surface: '#FFFFFF',
    ink: '#0F1E2B',
    radius: 'soft',
    menuLayout: 'shelf',
    heroVariant: 'lineup',
    cardStyle: 'flat',
    navStyle: 'pills',
    texture: 'none',
    buttonShape: 'pill',
    headingCase: 'normal',
    fontHeading: 'Gabarito',
    fontBody: 'Instrument Sans',
    canvasLight: '#EFF3F5',
    canvasDark: '#0B141B',
    fonts: 'Gabarito and Instrument Sans',
  },
  {
    id: 'docket',
    label: 'Docket',
    blurb: 'Your menu as a till receipt: a narrow paper roll on a counter of your brand colour, dot-matrix name, dashed rules. Kiosks, coffee carts, short menus.',
    bestFor: 'Kiosks, coffee carts',
    canvas: '#FBFBF9',
    surface: '#FFFFFF',
    ink: '#23222B',
    radius: 'sharp',
    menuLayout: 'board',
    heroVariant: 'receipt',
    cardStyle: 'bare',
    navStyle: 'tabs',
    texture: 'none',
    buttonShape: 'square',
    headingCase: 'upper',
    fontHeading: 'Doto',
    fontBody: 'DM Mono',
    canvasLight: '#FBFBF9',
    canvasDark: '#1B1A20',
    fonts: 'Doto and DM Mono',
  },
  {
    id: 'bistro',
    label: 'Bistro',
    blurb: 'Oxblood and plaster, italic serif, one dish per row. On a desktop your photo stays pinned beside the menu. Steak, Italian, date night.',
    bestFor: 'Steak, Italian, date night',
    canvas: '#1E0E10',
    surface: '#2A1517',
    ink: '#F3E4DC',
    radius: 'sharp',
    menuLayout: 'rows',
    heroVariant: 'side',
    cardStyle: 'bare',
    navStyle: 'tabs',
    texture: 'none',
    buttonShape: 'square',
    headingCase: 'normal',
    fontHeading: 'Bodoni Moda',
    fontBody: 'DM Sans',
    canvasLight: '#F4E9E4',
    canvasDark: '#1E0E10',
    fonts: 'Bodoni Moda and DM Sans',
    darkFirst: true,
    headingItalic: true,
  },
]

export function lookMeta(look: string): LookMeta {
  return LOOKS.find((l) => l.id === look) ?? LOOKS[0]
}

export const RADIUS_OPTIONS: Array<{ id: Exclude<StorefrontRadius, ''>; label: string }> = [
  { id: 'sharp', label: 'Sharp' },
  { id: 'soft', label: 'Soft' },
  { id: 'pill', label: 'Pill' },
]

export const MENU_LAYOUT_OPTIONS: Array<{ id: Exclude<StorefrontMenuLayout, ''>; label: string; hint: string }> = [
  { id: 'grid', label: 'Grid', hint: 'Photo beside the name, two columns on desktop' },
  { id: 'list', label: 'List', hint: 'Compact rows, small thumbnail or none' },
  { id: 'tiles', label: 'Tiles', hint: 'Photo on top, two or three across' },
  { id: 'board', label: 'Board', hint: 'A price list with dotted leaders, no photos' },
  { id: 'magazine', label: 'Magazine', hint: 'Each category opens on one big photo, the rest as photo tiles' },
  { id: 'gallery', label: 'Gallery', hint: 'Tall photos in staggered columns, name and price beneath' },
  { id: 'shelf', label: 'Shelf', hint: 'Each category is one row that scrolls sideways' },
  { id: 'rows', label: 'Rows', hint: 'One dish per row, large, photo and text side by side' },
]

export const HERO_VARIANT_OPTIONS: Array<{ id: Exclude<StorefrontHeroVariant, ''>; label: string; hint: string }> = [
  { id: 'split', label: 'Split', hint: 'Photo beside the name' },
  { id: 'cover', label: 'Cover', hint: 'Name over a full-width photo' },
  { id: 'type', label: 'Type', hint: 'No photo, big name' },
  { id: 'minimal', label: 'Minimal', hint: 'Slim header, menu first' },
  { id: 'carousel', label: 'Carousel', hint: 'Photos rotate' },
  { id: 'poster', label: 'Poster', hint: 'Your name edge to edge, photos in a strip below' },
  { id: 'collage', label: 'Collage', hint: 'Three photos in three shapes beside the name' },
  { id: 'fullbleed', label: 'Full width', hint: 'One photo from edge to edge, your name beneath it' },
  { id: 'billboard', label: 'Billboard', hint: 'Your name on a block of your brand colour, photo as a round sticker' },
  { id: 'lineup', label: 'Line-up', hint: 'Three photos standing on a shelf beside the name' },
  { id: 'receipt', label: 'Receipt', hint: 'No photo, your name and order times set like a till receipt' },
  { id: 'side', label: 'Side photo', hint: 'On a desktop the photo stays pinned beside the menu' },
]

export const BUTTON_SHAPE_OPTIONS: Array<{ id: Exclude<StorefrontButtonShape, ''>; label: string; hint: string }> = [
  { id: 'pill', label: 'Pill', hint: 'Fully rounded ends' },
  { id: 'rounded', label: 'Rounded', hint: 'Softly rounded corners' },
  { id: 'square', label: 'Square', hint: 'Crisp, nearly square edges' },
]

export const HEADING_CASE_OPTIONS: Array<{ id: Exclude<StorefrontHeadingCase, ''>; label: string; hint: string }> = [
  { id: 'normal', label: 'As typed', hint: 'Headings keep your capitalisation' },
  { id: 'upper', label: 'Capitals', hint: 'Headings set in capitals, poster style' },
]

export const BACKGROUND_FIT_OPTIONS: Array<{ id: Exclude<StorefrontBackgroundFit, ''>; label: string; hint: string }> = [
  { id: 'cover', label: 'Fill the screen', hint: 'One photo behind the whole page' },
  { id: 'tile', label: 'Repeat as a pattern', hint: 'A small image repeated, like a tile or print' },
]

export const BACKGROUND_STRENGTH_OPTIONS: Array<{
  id: Exclude<StorefrontBackgroundStrength, ''>
  label: string
  hint: string
}> = [
  { id: 'faint', label: 'Faint', hint: 'A whisper of texture' },
  { id: 'soft', label: 'Soft', hint: 'Visible, calm behind the menu' },
  { id: 'strong', label: 'Strong', hint: 'The picture reads clearly' },
]

/**
 * Every font a tenant can pick, grouped for the picker. Mirrors the allowlist
 * in storefront/src/lib/fonts.ts (FONTS); change both together.
 */
export const FONT_CATALOGUE: Array<{ name: string; group: 'Sans' | 'Serif' | 'Display' }> = [
  { name: 'Inter', group: 'Sans' },
  { name: 'DM Sans', group: 'Sans' },
  { name: 'Manrope', group: 'Sans' },
  { name: 'Plus Jakarta Sans', group: 'Sans' },
  { name: 'Figtree', group: 'Sans' },
  { name: 'Karla', group: 'Sans' },
  { name: 'Nunito', group: 'Sans' },
  { name: 'Poppins', group: 'Sans' },
  { name: 'Outfit', group: 'Sans' },
  { name: 'Sora', group: 'Sans' },
  { name: 'Rubik', group: 'Sans' },
  { name: 'Epilogue', group: 'Sans' },
  { name: 'Space Grotesk', group: 'Sans' },
  { name: 'Archivo', group: 'Sans' },
  { name: 'Jost', group: 'Sans' },
  { name: 'Instrument Sans', group: 'Sans' },
  { name: 'DM Mono', group: 'Sans' },
  { name: 'Fraunces', group: 'Serif' },
  { name: 'Instrument Serif', group: 'Serif' },
  { name: 'Cormorant Garamond', group: 'Serif' },
  { name: 'Playfair Display', group: 'Serif' },
  { name: 'DM Serif Display', group: 'Serif' },
  { name: 'Lora', group: 'Serif' },
  { name: 'Bodoni Moda', group: 'Serif' },
  { name: 'Bricolage Grotesque', group: 'Display' },
  { name: 'Big Shoulders Display', group: 'Display' },
  { name: 'Unbounded', group: 'Display' },
  { name: 'Syne', group: 'Display' },
  { name: 'Oswald', group: 'Display' },
  { name: 'Anton', group: 'Display' },
  { name: 'Baloo Bhaijaan 2', group: 'Display' },
  { name: 'Gabarito', group: 'Display' },
  { name: 'Doto', group: 'Display' },
]

/**
 * One Google Fonts stylesheet with every catalogue face, subset to the
 * letters of `sample`, so the picker can show each name in its own face for
 * a few kilobytes.
 */
export function fontPreviewHref(sample: string): string {
  const letters = Array.from(new Set(`${sample}AaBbCcDdEeFfGgHhIiJjKkLlMmNnOoPpQqRrSsTtUuVvWwXxYyZz0123456789&`)).join('')
  const families = FONT_CATALOGUE.map((f) => `family=${f.name.replace(/ /g, '+')}`).join('&')
  return `https://fonts.googleapis.com/css2?${families}&text=${encodeURIComponent(letters)}&display=swap`
}

/** The bare origin of an absolute http(s) URL, or null. */
export function originOf(raw: string): string | null {
  try {
    const u = new URL(raw.trim())
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.origin : null
  } catch {
    return null
  }
}

export const CARD_STYLE_OPTIONS: Array<{ id: Exclude<StorefrontCardStyle, ''>; label: string; hint: string }> = [
  { id: 'raised', label: 'Raised', hint: 'White cards with a soft shadow' },
  { id: 'outline', label: 'Outline', hint: 'A thin border, no fill' },
  { id: 'flat', label: 'Flat', hint: 'A tinted fill, no border' },
  { id: 'bare', label: 'Bare', hint: 'No card at all, items sit on the page' },
  { id: 'sticker', label: 'Sticker', hint: 'A drawn outline and a hard shadow, like a label' },
]

export const NAV_STYLE_OPTIONS: Array<{ id: Exclude<StorefrontNavStyle, ''>; label: string; hint: string }> = [
  { id: 'pills', label: 'Pills', hint: 'Filled chips' },
  { id: 'tabs', label: 'Tabs', hint: 'Underlined words' },
  { id: 'index', label: 'Side index', hint: 'A sticky category list beside the menu on desktop, tabs on phones' },
]

export const TEXTURE_OPTIONS: Array<{ id: Exclude<StorefrontTexture, ''>; label: string; hint: string }> = [
  { id: 'none', label: 'None', hint: 'A clean canvas' },
  { id: 'grain', label: 'Grain', hint: 'Fine film grain over the page' },
  { id: 'paper', label: 'Paper', hint: 'A paper fibre under the page' },
]

/**
 * A link to the live storefront showing unsaved design choices. The storefront
 * reads `sf_*` params into this browser tab only (storefront/src/lib/looks.ts
 * readDesignPreview); nothing is saved and no other visitor sees it.
 */
export function previewUrl(
  base: string,
  d: {
    look: string
    radius: string
    menuLayout: string
    heroVariant: string
    cardStyle: string
    navStyle: string
    texture: string
    fontHeading: string
    fontBody: string
    buttonShape: string
    headingCase: string
    brand: string
    accent: string
    canvasLight: string
    canvasDark: string
  },
): string | null {
  const b = base.trim().replace(/\/+$/, '')
  if (!/^https?:\/\//i.test(b)) return null
  const q = new URLSearchParams()
  const set = (k: string, v: string) => {
    if (v) q.set(k, v)
  }
  set('sf_look', d.look || 'counter')
  set('sf_radius', d.radius)
  set('sf_menu', d.menuLayout)
  set('sf_hero', d.heroVariant)
  set('sf_card', d.cardStyle)
  set('sf_nav', d.navStyle)
  set('sf_texture', d.texture)
  set('sf_font_heading', d.fontHeading)
  set('sf_font_body', d.fontBody)
  set('sf_button', d.buttonShape)
  set('sf_case', d.headingCase)
  // Colours travel without the '#', which would start the URL fragment.
  const hex = (v: string) => (/^#[0-9a-fA-F]{6}$/.test(v) ? v.slice(1) : '')
  set('sf_brand', hex(d.brand))
  set('sf_accent', hex(d.accent))
  set('sf_canvas', hex(d.canvasLight))
  set('sf_canvas_dark', hex(d.canvasDark))
  return `${b}/?${q.toString()}`
}

export const SECTION_META: Record<StorefrontSectionId, { label: string; hint: string }> = {
  featured: { label: 'Featured items', hint: 'The rail of items you pick under Featured items below.' },
  deals: { label: 'Deals', hint: 'Active promo codes customers can tap to apply.' },
  menu: { label: 'Menu', hint: 'Your categories and items. Always shown.' },
  about: { label: 'About', hint: 'Your story, in your words.' },
  gallery: { label: 'Gallery', hint: 'Shows every image in your library.' },
  reviews: { label: 'Reviews', hint: 'Shows your 4- and 5-star Google reviews from the Reputation Suite publicly.' },
  hours: { label: 'Hours', hint: 'Shows your online-ordering hours table.' },
  loyalty: {
    label: 'Loyalty',
    hint: 'Links to your loyalty sign-up page. Needs the loyalty programme on and a public POS address set.',
  },
}

export function readConfig(v: unknown): StorefrontConfig {
  if (v && typeof v === 'object') return v as StorefrontConfig
  if (typeof v === 'string') {
    try {
      return JSON.parse(v) as StorefrontConfig
    } catch {
      return {}
    }
  }
  return {}
}

export function readImages(v: unknown): StorefrontImage[] {
  let arr: unknown = v
  if (typeof v === 'string') {
    try {
      arr = JSON.parse(v)
    } catch {
      return []
    }
  }
  if (!Array.isArray(arr)) return []
  return (arr as Array<Partial<StorefrontImage>>)
    .filter((i) => i && typeof i.id === 'string' && typeof i.src === 'string' && i.src.trim() !== '')
    .slice(0, MAX_STOREFRONT_IMAGES)
    .map((i) => ({ id: i.id!, src: i.src!, caption: typeof i.caption === 'string' ? i.caption : '' }))
}

export function newImageId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  } catch {
    /* fall through */
  }
  return Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join('')
}
